import { Property } from 'generated/graphql'
import React from 'react'
import { VscDash, VscAdd, VscChevronUp, VscChevronDown, VscCircleOutline } from 'react-icons/vsc'
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Text,
  List,
  ListItem,
  Center,
  ListIcon
} from '@chakra-ui/react'
import {
  FaBath,
  FaBed,
  FaCalendar,
  FaCar,
  FaCircle,
  FaCouch,
  FaHome,
  FaKey,
  FaMapPin,
  FaRuler,
  FaWater
} from 'react-icons/fa'
import moment from 'moment'
import { DATE_FORMAT } from '../../../../constants'
import { useTranslation } from 'react-i18next'

type PropertyOverviewProps = {
  property: Property
}

const PropertyOverview: React.FC<PropertyOverviewProps> = ({ property }) => {
  const buttonStyles = {
    px: 0,
    fontWeight: 'extrabold',
    fontSize: 'xl',
    color: 'gray.500',
    _hover: {
      bg: 'transparent'
    }
  }
  const { t, i18n } = useTranslation()
  const tanslator = i18n as any
  const lang = tanslator.language as string
  return (
    <>
      <AccordionItem p={0} border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton {...buttonStyles}>
              {isExpanded ? <VscDash /> : <VscAdd />}
              <Text textAlign="left" width="100%" mx={2}>
                {t('property_details.property_info')}
              </Text>
              {isExpanded ? <VscChevronUp /> : <VscChevronDown />}
            </AccordionButton>

            <AccordionPanel px={0} pb={8}>
              <List spacing={4}>
                {property?.slug && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaKey} color="blue.500" />
                        <Text mx={2}>{t('property_details.ref_number')}</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold" noOfLines={1}>
                        {property?.reference_number}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {(property?.type_of_property as any)?.[`name_${lang}`] && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaHome} color="blue.500" />
                        <Text mx={2}>{t('property_details.type_of_property')}</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {(property?.type_of_property as any)?.[`name_${lang}`]}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.occupation_date && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaCalendar} color="blue.500" />
                        <Text mx={2}>{t('property_details.occupation_date')}</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {moment(property?.occupation_date).format(DATE_FORMAT)}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.floor_size && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaRuler} color="blue.500" />
                        <Text mx={2}>{t('property_details.floor_size')}</Text>
                      </Center>
                      <Text fontSize="xs" mx={2} fontWeight="extrabold">
                        {property?.floor_size} m
                        <Text as="sup" fontSize="xs">
                          2
                        </Text>
                      </Text>
                    </Center>
                  </ListItem>
                )}

                <ListItem>
                  <Center justifyContent="space-between">
                    <Center fontSize="xs">
                      <ListIcon as={FaCouch} color="blue.500" />
                      <Text mx={2}>{t('property_details.furnished')}</Text>
                    </Center>
                    <Text fontSize="xs" fontWeight="extrabold">
                      {property?.furnished ? 'Oui' : 'Non'}
                    </Text>
                  </Center>
                </ListItem>

                {property?.street_address && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center minW="fit-content" fontSize="xs">
                        <ListIcon as={FaMapPin} color="blue.500" />
                        <Text mx={2}>{t('property_details.street_address')}: </Text>
                      </Center>
                      <Text fontSize="xs" noOfLines={1} fontWeight="extrabold">
                        {property?.street_address}
                      </Text>
                    </Center>
                  </ListItem>
                )}
              </List>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton {...buttonStyles}>
              {isExpanded ? <VscDash /> : <VscAdd />}
              <Text textAlign="left" width="100%" mx={2}>
                {t('property_details.room')}s
              </Text>
              {isExpanded ? <VscChevronUp /> : <VscChevronDown />}
            </AccordionButton>

            <AccordionPanel px={0} pb={8}>
              <List spacing={4}>
                {property?.bedrooms && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaBed} color="blue.500" />
                        <Text mx={2}>{t('property_details.room')}(s)</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.bedrooms}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.bathrooms && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaBath} color="blue.500" />
                        <Text mx={2}> {t('property_details.bathroom')}</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.bathrooms}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.kitchens && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaCircle} color="blue.500" />
                        <Text mx={2}>{t('property_details.kitchen')}(s)</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.kitchens}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.dining_rooms && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={VscCircleOutline} color="blue.500" />
                        <Text mx={2}>{t('property_details.dining_room')}</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.dining_rooms}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.lounges && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaCouch} color="blue.500" />
                        <Text mx={2}>{t('property_details.living_room')}(s)</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.lounges}
                      </Text>
                    </Center>
                  </ListItem>
                )}
                {property?.parking && (
                  <ListItem>
                    <Center justifyContent="space-between">
                      <Center fontSize="xs">
                        <ListIcon as={FaCar} color="blue.500" />
                        <Text mx={2}>{t('property_details.parking')}(s)</Text>
                      </Center>
                      <Text fontSize="xs" fontWeight="extrabold">
                        {property?.parking}
                      </Text>
                    </Center>
                  </ListItem>
                )}
              </List>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton {...buttonStyles}>
              {isExpanded ? <VscDash /> : <VscAdd />}
              <Text textAlign="left" width="100%" mx={2}>
                {t('property_details.internal_features')}
              </Text>
              {isExpanded ? <VscChevronUp /> : <VscChevronDown />}
            </AccordionButton>

            <AccordionPanel px={0} pb={8}>
              <List spacing={4}>
                <ListItem>
                  <Center justifyContent="space-between">
                    <Center fontSize="xs">
                      <ListIcon as={FaWater} color="blue.500" />
                      <Text mx={2}>{t('property_details.pool')}</Text>
                    </Center>
                    <Text fontSize="xs" fontWeight="extrabold">
                      {property?.pool ? 'Oui' : 'Non'}
                    </Text>
                  </Center>
                </ListItem>
              </List>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </>
  )
}

export default PropertyOverview
